import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import clsx from "clsx";
import { Heading, Text } from "components/common";
import { generateRandomString } from "utils/generateRandomString";
import { IoSend } from "react-icons/io5";
import classes from "./HeroSection.module.css";

const TYPING_TEXTS = [
  "Unleash your darkest thoughts...",
  "Ask the forbidden questions...",
  "Explore without limits...",
  "Get unfiltered answers...",
  "Break free from censorship...",
  "Dive into the shadows...",
];

const FEATURES = [
  {
    title: "Unfiltered AI",
    highlight: "No Censorship",
    info: "Experience raw, unfiltered conversations without any restrictions or limitations.",
  },
  {
    title: "Zero Traces",
    highlight: "Complete Privacy",
    info: "Your conversations vanish into the void. No logs, no history, no digital footprints.",
  },
  {
    title: "Dark Insights",
    highlight: "Deep Knowledge",
    info: "Access forbidden knowledge and explore topics others won't discuss.",
  },
  {
    title: "Shadow Mode",
    highlight: "True Freedom",
    info: "Speak freely without fear of judgment or consequences.",
  },
];

const STATS = [
  { label: "Active Users", value: "10K+" },
  { label: "Messages/Day", value: "1M+" },
];

const HeroSection = () => {
  const navigate = useNavigate();
  const textareaRef = useRef(null);
  const [question, setQuestion] = useState("");
  const [typingText, setTypingText] = useState("");
  const [textIndex, setTextIndex] = useState(0);
  const [charIndex, setCharIndex] = useState(0);

  const handleGenerate = () => {
    if (!question.trim()) return;
    const id = generateRandomString(25);
    navigate(`/chat/${id}?prompt=${encodeURIComponent(question.trim())}`);
  };

  const handleTextareaChange = (e) => {
    setQuestion(e.target.value);
    // Adjust textarea height
    e.target.style.height = 'auto';
    e.target.style.height = Math.min(e.target.scrollHeight, 200) + 'px';
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (question.trim()) {
        handleGenerate();
      }
    }
  };

  useEffect(() => {
    const TYPING_SPEED = 50;
    const PAUSE_DURATION = 3000;
    let typingTimer;

    const typeText = () => {
      if (charIndex < TYPING_TEXTS[textIndex].length) {
        setTypingText(prev => TYPING_TEXTS[textIndex].substring(0, charIndex + 1));
        setCharIndex(prev => prev + 1);
      } else {
        clearInterval(typingTimer);
        setTimeout(() => {
          setTypingText("");
          setCharIndex(0);
          setTextIndex(prev => (prev + 1) % TYPING_TEXTS.length);
        }, PAUSE_DURATION);
      }
    };

    typingTimer = setInterval(typeText, TYPING_SPEED);
    return () => clearInterval(typingTimer);
  }, [textIndex, charIndex]);

  return (
    <section className={clsx(classes.wrapper, "sectionBg")}>
      <div className={classes.container}>
        <div className={classes.heroContent}>
          <div className={classes.headingContainer}>
            <Heading xl6 gradient>
              Unleash the Shadows of AI
            </Heading>
            <Text xl primitive300 className="mt-4">
              Step into a realm where AI knows no bounds. Your gateway to 
              uncensored, unfiltered conversations that disappear without a trace.
            </Text>
          </div>

          <div className={classes.inputWrapper}>
            <div className={classes.inputContainer}>
              <textarea
                ref={textareaRef}
                className={classes.textarea}
                value={question}
                onChange={handleTextareaChange}
                onKeyDown={handleKeyDown}
                placeholder={typingText || "Send a message..."}
                rows={1}
              />
              <div className={classes.buttonContainer}>
                <Text className={classes.hintText}>
                  Press Enter to send, Shift + Enter for new line
                </Text>
                <button
                  className={classes.sendButton}
                  onClick={handleGenerate}
                  disabled={!question.trim()}
                >
                  Send
                  <IoSend className={classes.sendIcon} />
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className={classes.heroVisual}>
          {FEATURES.map((feature, index) => (
            <div 
              className={classes.feature} 
              key={index}
              data-aos="fade-up"
              data-aos-delay={index * 100}
            >
              <div className={classes.heading}>
                <Heading xl2 medium gradient>
                  {feature.highlight}
                </Heading>
                <Heading xl3 medium primitiveDefault>
                  {feature.title}
                </Heading>
              </div>
              <Text base primitive400>
                {feature.info}
              </Text>
            </div>
          ))}

          {STATS.map((stat, index) => (
            <div key={index} className={classes.stats}>
              <Heading xl2 gradient>
                {stat.value}
              </Heading>
              <Text base primitive300>
                {stat.label}
              </Text>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default HeroSection;